import { useState } from "react";
import { Check, X, ListChecks, CalendarRange, AlertTriangle, ChevronLeft, ChevronRight, Layers } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getCategory } from "@/lib/categories";
import { pad2 } from "@/lib/time";
import { BULK_CONFIG } from "@/lib/bulkConfig";
import { useTranslation } from "@/i18n";

function fmt(h, m) {
  if (h == null || m == null) return "—";
  return `${pad2(h)}:${pad2(m)}`;
}

// Compact card for a large (bulk) calendar proposal. Shows a summary + a small
// preview, and only renders a paginated slice of events on "Review details" —
// never all rows at once. Works on desktop and mobile.
export default function BulkProposalCard({ proposal, onApproveAll, onReject, busy }) {
  const { t } = useTranslation();
  const [reviewing, setReviewing] = useState(false);
  const [page, setPage] = useState(0);

  const vs = proposal.validationStatus || {};
  const canApprove = vs.valid && proposal.estimatedEventCount > 0 && !busy;
  const pageSize = BULK_CONFIG.reviewPageSize;
  const pageCount = Math.max(1, Math.ceil(proposal.events.length / pageSize));
  const pageItems = proposal.events.slice(page * pageSize, page * pageSize + pageSize);
  const cats = Object.entries(proposal.categorySummary || {});

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`bulk-card-${proposal.proposalId}`}>
      <div className="px-4 py-3 border-b border-border bg-slate-50/60">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-slate-600" />
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{t("bulk.bulkPlan")}</p>
        </div>
        <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate" data-testid="bulk-title">{proposal.title}</p>
      </div>

      <div className="px-4 py-3 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{t("bulk.totalEvents")}</p>
          <p className="font-semibold text-slate-900" data-testid="bulk-count">{proposal.estimatedEventCount}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{t("bulk.dateRange")}</p>
          <p className="font-medium text-slate-800 inline-flex items-center gap-1">
            <CalendarRange className="h-3.5 w-3.5 text-slate-500" />
            {proposal.dateRange?.start || "—"} → {proposal.dateRange?.end || "—"}
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t("bulk.categories")}</p>
          <div className="flex flex-wrap gap-1.5" data-testid="bulk-categories">
            {cats.map(([id, n]) => (
              <span key={id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getCategory(id).hex }} />
                {getCategory(id).label} · {n}
              </span>
            ))}
          </div>
        </div>
        {proposal.conflictSummary?.conflictCount > 0 ? (
          <div className="col-span-2 inline-flex items-center gap-1.5 text-amber-700 text-xs" data-testid="bulk-conflicts">
            <AlertTriangle className="h-3.5 w-3.5" /> {t("bulk.conflicts", { count: proposal.conflictSummary.conflictCount })}
          </div>
        ) : null}
        {!vs.valid ? (
          <div className="col-span-2 rounded-lg bg-rose-50 text-rose-700 text-xs px-3 py-2" data-testid="bulk-invalid">
            {t("bulk.invalidSummary", { count: vs.invalidCount || 0 })}
            {vs.overLimit ? ` · ${t("bulk.overLimit", { max: BULK_CONFIG.maxEventsPerJob })}` : ""}
          </div>
        ) : null}
      </div>

      {/* Preview: only the first N events */}
      {!reviewing ? (
        <ul className="divide-y divide-border border-t border-border" data-testid="bulk-preview">
          {proposal.previewEvents.map((e) => (
            <li key={e.sourceEventId} className="px-4 py-2 flex items-center gap-2.5 text-sm">
              <span className="h-6 w-6 rounded-md shrink-0" style={{ backgroundColor: getCategory(e.category).hex }} />
              <span className="flex-1 min-w-0 truncate text-slate-800">{e.title}</span>
              <span className="text-xs text-slate-500 font-mono-tab">{e.date} {fmt(e.startHour, e.startMinute)}</span>
            </li>
          ))}
          {proposal.estimatedEventCount > proposal.previewEvents.length ? (
            <li className="px-4 py-2 text-xs text-slate-500">
              {t("bulk.andMore", { count: proposal.estimatedEventCount - proposal.previewEvents.length })}
            </li>
          ) : null}
        </ul>
      ) : (
        <div className="border-t border-border" data-testid="bulk-review">
          <ul className="divide-y divide-border max-h-72 overflow-y-auto">
            {pageItems.map((e) => (
              <li key={e.sourceEventId} className="px-4 py-2 flex items-center gap-2.5 text-sm">
                <span className="h-6 w-6 rounded-md shrink-0" style={{ backgroundColor: getCategory(e.category).hex }} />
                <span className="flex-1 min-w-0 truncate text-slate-800">{e.title}</span>
                <span className="text-xs text-slate-500 font-mono-tab">{e.date} {fmt(e.startHour, e.startMinute)}–{fmt(e.endHour, e.endMinute)}</span>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 flex items-center justify-between bg-slate-50/60">
            <Button size="sm" variant="ghost" className="rounded-full" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="bulk-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-slate-500">{t("bulk.pageOf", { page: page + 1, total: pageCount })}</span>
            <Button size="sm" variant="ghost" className="rounded-full" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} data-testid="bulk-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="px-4 py-3 flex flex-wrap items-center gap-2 bg-slate-50/60 border-t border-border">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setReviewing((r) => !r)} data-testid="bulk-review-toggle">
          <ListChecks className="h-3.5 w-3.5 mr-1" /> {reviewing ? t("bulk.hideDetails") : t("bulk.reviewDetails")}
        </Button>
        <span className="flex-1" />
        <Button size="sm" variant="outline" className="rounded-full" onClick={onReject} data-testid="bulk-reject">
          <X className="h-3.5 w-3.5 mr-1" /> {t("bulk.reject")}
        </Button>
        <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={!canApprove} onClick={onApproveAll} data-testid="bulk-approve-all">
          <Check className="h-3.5 w-3.5 mr-1" /> {t("bulk.approveAll", { count: proposal.estimatedEventCount })}
        </Button>
      </div>
    </div>
  );
}
